/* MonthlyStaffingStack — stacked monthly staffed-capacity bar chart,
 * the staffing sibling of MonthlyRevenueStack. Reused on the Project
 * detail page (one segment per staffed person) and the Client detail
 * page (one segment per project).
 *
 * Input is segment-major: an array of series, each carrying a label and
 * a { "YYYY-MM": days } map. The values are staffed person-days per
 * month — the same metric the project monthly table shows (1 day = 8 h,
 * derived 1:1 from staffing %). Stacked, so a month's total bar height
 * is the total staffed capacity that month.
 *
 * The x window is data-driven: it spans the first month with any data
 * to the last (filling gaps with empty bars so the timeline is
 * continuous), capped to the most recent MAX_MONTHS so a long-running
 * engagement doesn't produce a hundred bars.
 */
import React from 'react';
import { InteractiveChart } from './interactive-chart.jsx';

const MAX_MONTHS = 24;

/** Inclusive list of "YYYY-MM" keys from start to end. */
function monthKeysBetween(startYm, endYm) {
  const [sy, sm] = startYm.split('-').map(Number);
  const [ey, em] = endYm.split('-').map(Number);
  const out = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
}

/**
 * Build the stacked-bar data + series for a list of staffing segments.
 *
 * @param {Array} series  [{ key, label, byMonth: { "YYYY-MM": days } }]
 * @returns {{ data: Array, stacks: Array<{key,label}> }}
 */
export function buildMonthlyStaffingData(series, maxMonths = MAX_MONTHS) {
  const months = new Set();
  for (const s of (series || [])) {
    for (const [ym, days] of Object.entries(s.byMonth || {})) {
      if (days > 0) months.add(ym);
    }
  }
  const sorted = [...months].sort();
  if (!sorted.length) return { data: [], stacks: [] };

  let window = monthKeysBetween(sorted[0], sorted[sorted.length - 1]);
  if (window.length > maxMonths) window = window.slice(-maxMonths);
  const windowSet = new Set(window);

  // Keep only segments with a value inside the visible window.
  const stacks = (series || [])
    .filter(s => Object.entries(s.byMonth || {}).some(([ym, d]) => windowSet.has(ym) && d > 0))
    .map(s => ({ key: s.key, label: s.label, byMonth: s.byMonth }));

  const data = window.map(ym => {
    const row = { name: ym };
    for (const s of stacks) row[s.key] = Math.round((s.byMonth[ym] || 0) * 100) / 100;
    return row;
  });

  return { data, stacks: stacks.map(s => ({ key: s.key, label: s.label })) };
}

/**
 * <MonthlyStaffingStack /> — the rendered chart.
 */
export const MonthlyStaffingStack = ({ title, series, footnote }) => {
  const { data, stacks } = React.useMemo(() => buildMonthlyStaffingData(series), [series]);

  const hasAnyValue = data.some(row => stacks.some(s => row[s.key] > 0));
  if (!hasAnyValue) {
    return (
      <div style={{padding:'18px 0',color:'var(--fg-muted)',fontSize:13}}>
        No staffing to plot yet. Allocate people on the Staffing page and this chart will populate.
      </div>
    );
  }

  return (
    <InteractiveChart
      spec={{
        type: 'bar',
        title,
        data,
        xKey: 'name',
        stacked: true,
        series: stacks.map(s => ({ key: s.key, name: s.label })),
        height: 280,
        footnote: footnote || 'Staffed person-days per month (1 day = 8 h, derived from staffing %). A week counts toward the month of its Monday.',
      }}
    />
  );
};
